/**
 * 限价美分步长（tick）：对齐 PM CLOB minimum_tick_size。
 * - 有官方美元 tick → 换算美分
 * - 否则从订单簿档位推断：全整数¢ → 1；出现小数 → 0.1
 * - 空簿默认 1¢（PM 安全）
 */

/** 空簿 / 未知时的保守步长（1¢ = PM tick 0.01） */
export const DEFAULT_LIMIT_TICK_CENTS = 1;

/** 盘口出现小数美分时的步长（0.1¢ = PM tick 0.001） */
export const FINE_LIMIT_TICK_CENTS = 0.1;

const KNOWN_TICK_DOLLARS = [0.1, 0.01, 0.001, 0.0001];

/**
 * @param {unknown[]} bookPrices 盘口美分价（ask/bid 档）
 * @returns {number[]}
 */
export function collectOutcomeBookPriceCents(outcomes = []) {
  const out = [];
  const list = Array.isArray(outcomes) ? outcomes : [];
  for (const o of list) {
    for (const key of ['askCents', 'bidCents']) {
      const n = Number(o?.[key]);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  }
  return out;
}

/**
 * @param {unknown} tickSizeDollars PM minimum_tick_size（0~1）
 * @returns {number|null} 美分步长
 */
export function tickDollarsToCents(tickSizeDollars) {
  const d = Number(tickSizeDollars);
  if (!Number.isFinite(d) || d <= 0) return null;
  const matched = KNOWN_TICK_DOLLARS.find((t) => Math.abs(t - d) < 1e-12);
  const dollars = matched ?? d;
  const cents = dollars * 100;
  if (!(cents > 0) || !Number.isFinite(cents)) return null;
  // 规整浮点：0.1 / 1 / 10 …
  if (cents >= 1) return Math.round(cents * 1e6) / 1e6;
  return Math.round(cents * 1e6) / 1e6;
}

/**
 * 从盘口美分价推断步长。
 * @param {unknown[]} bookPrices
 * @returns {number|null} 有有效价时返回 1 或 0.1；空则 null
 */
export function inferLimitTickCentsFromBook(bookPrices = []) {
  const prices = (Array.isArray(bookPrices) ? bookPrices : [])
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
  if (!prices.length) return null;
  const hasFractional = prices.some((p) => Math.abs(p - Math.round(p)) > 1e-6);
  return hasFractional ? FINE_LIMIT_TICK_CENTS : DEFAULT_LIMIT_TICK_CENTS;
}

/**
 * @param {{
 *   bookPrices?: unknown[],
 *   outcomes?: unknown[],
 *   tickSizeDollars?: unknown,
 *   fallbackCents?: number,
 * }} [opts]
 * @returns {number} 美分步长（>0）
 */
export function resolveLimitTickCents(opts = {}) {
  const fromOfficial = tickDollarsToCents(opts.tickSizeDollars);
  if (fromOfficial != null) return fromOfficial;

  const book =
    Array.isArray(opts.bookPrices) && opts.bookPrices.length
      ? opts.bookPrices
      : collectOutcomeBookPriceCents(opts.outcomes);
  const inferred = inferLimitTickCentsFromBook(book);
  if (inferred != null) return inferred;

  const fb = Number(opts.fallbackCents);
  return Number.isFinite(fb) && fb > 0 ? fb : DEFAULT_LIMIT_TICK_CENTS;
}

/**
 * 将美分价吸附到步长，并夹在合法区间。
 * @param {unknown} n
 * @param {number} [step=DEFAULT_LIMIT_TICK_CENTS]
 * @returns {number}
 */
export function snapTradeCents(n, step = DEFAULT_LIMIT_TICK_CENTS) {
  const tick = Number(step);
  const s = Number.isFinite(tick) && tick > 0 ? tick : DEFAULT_LIMIT_TICK_CENTS;
  const num = Number(n);
  const base = Number.isFinite(num) ? num : 50;
  const snapped = Math.round(base / s) * s;
  const decimals = s < 1 ? Math.max(0, Math.round(-Math.log10(s))) : 0;
  const fixed =
    decimals > 0
      ? Math.round(snapped * 10 ** decimals) / 10 ** decimals
      : Math.round(snapped);
  if (s >= 1) return Math.min(99, Math.max(1, fixed));
  return Math.min(99.9, Math.max(0.1, fixed));
}

/**
 * 限价输入展示（不含 ¢）。
 * @param {unknown} cents
 * @param {number} [step=DEFAULT_LIMIT_TICK_CENTS]
 * @returns {string}
 */
export function formatLimitCentsDisplay(cents, step = DEFAULT_LIMIT_TICK_CENTS) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  const tick = Number(step);
  const s = Number.isFinite(tick) && tick > 0 ? tick : DEFAULT_LIMIT_TICK_CENTS;
  const snapped = snapTradeCents(n, s);
  if (s >= 1) return String(Math.round(snapped));
  const decimals = Math.max(0, Math.round(-Math.log10(s)));
  return snapped.toFixed(decimals);
}

/** 步长 ≥1¢ 时禁止小数点 */
export function limitTickAllowsDecimal(step) {
  const s = Number(step);
  return Number.isFinite(s) && s > 0 && s < 1;
}
